import React, { useEffect, useMemo, useRef, useState } from 'react';
import { arrayOf, bool, func, node, object, shape, string } from 'prop-types';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';

import ValidationError from '../ValidationError/ValidationError';
import OutsideClickHandler from '../OutsideClickHandler/OutsideClickHandler';
import { FieldLabel } from '../FieldTextInput/FieldLabel/FieldLabel';

import css from './FieldSearchableSelect.module.css';

/**
 * Searchable single-select (Inbox listing filter pattern) for Final Form.
 * Options: { value, label, meta? }
 */
const FieldSearchableSelectComponent = props => {
  const {
    rootClassName,
    className,
    id,
    label,
    input,
    meta,
    options,
    placeholder,
    searchPlaceholder,
    disabled,
    clearable,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const onKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const selected = useMemo(
    () => (options || []).find(option => option.value === input.value) || null,
    [options, input.value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return options || [];
    }
    return (options || []).filter(option => {
      const haystack = `${option.label || ''} ${option.meta || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [options, query]);

  const { valid, invalid, touched, error } = meta;
  const hasError = touched && invalid && error;

  const selectOption = value => {
    input.onChange(value);
    input.onBlur();
    setIsOpen(false);
    setQuery('');
  };

  const clearSelection = event => {
    event.preventDefault();
    event.stopPropagation();
    input.onChange('');
    input.onBlur();
    setIsOpen(false);
    setQuery('');
  };

  const classes = classNames(rootClassName || css.root, className);
  const triggerLabel = selected?.label || placeholder;

  return (
    <div className={classes}>
      {label ? <FieldLabel label={label} id={id} /> : null}
      <OutsideClickHandler className={css.control} onOutsideClick={() => setIsOpen(false)}>
        <div className={css.triggerWrap}>
          <button
            type="button"
            id={id}
            className={classNames(
              css.trigger,
              hasError && css.triggerError,
              selected && css.triggerHasValue
            )}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            disabled={disabled}
            onClick={() => {
              if (!disabled) {
                setIsOpen(open => !open);
              }
            }}
            onBlur={input.onBlur}
          >
            <span className={classNames(css.triggerLabel, !selected && css.placeholder)}>
              {triggerLabel}
              {selected?.meta ? <span className={css.triggerMeta}>{selected.meta}</span> : null}
            </span>
          </button>
          <div className={css.endIcons}>
            {clearable && selected && !disabled ? (
              <button
                type="button"
                className={css.clearButton}
                aria-label="Clear"
                onClick={clearSelection}
              >
                <CloseIcon fontSize="small" />
              </button>
            ) : null}
            <KeyboardArrowDownIcon className={css.chevron} aria-hidden />
          </div>
        </div>
        {isOpen ? (
          <div className={css.popup} role="dialog" aria-label={placeholder}>
            <input
              ref={searchInputRef}
              className={css.searchInput}
              type="search"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
            />
            <ul className={css.optionList} role="listbox">
              {filtered.map(option => {
                const isSelected = option.value === input.value;
                return (
                  <li key={option.value}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={css.optionButton}
                      onClick={() => selectOption(option.value)}
                    >
                      <span className={css.optionLabel}>
                        {option.label}
                        {option.meta ? <span className={css.optionMeta}>{option.meta}</span> : null}
                      </span>
                      {isSelected ? (
                        <CheckIcon className={css.optionCheck} fontSize="small" />
                      ) : null}
                    </button>
                  </li>
                );
              })}
              {filtered.length === 0 ? <li className={css.emptyRow}>—</li> : null}
            </ul>
          </div>
        ) : null}
      </OutsideClickHandler>
      <ValidationError fieldMeta={meta} />
    </div>
  );
};

FieldSearchableSelectComponent.defaultProps = {
  rootClassName: null,
  className: null,
  id: null,
  label: null,
  options: [],
  placeholder: '',
  searchPlaceholder: 'Search',
  disabled: false,
  clearable: false,
};

FieldSearchableSelectComponent.propTypes = {
  rootClassName: string,
  className: string,
  id: string,
  label: node,
  input: object.isRequired,
  meta: object.isRequired,
  options: arrayOf(
    shape({
      value: string.isRequired,
      label: string.isRequired,
      meta: string,
    })
  ),
  placeholder: string,
  searchPlaceholder: string,
  disabled: bool,
  clearable: bool,
};

const FieldSearchableSelect = props => {
  return <Field component={FieldSearchableSelectComponent} {...props} />;
};

export default FieldSearchableSelect;
